import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Tab from "@mui/material/Tab"
import TableCell from "@mui/material/TableCell"
import Tabs from "@mui/material/Tabs"
import TextField from "@mui/material/TextField"
import { format } from "date-fns"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import Column from "../../../common/components/Column"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import { useNavigateUpdatedSearchParams } from "../../../common/utils"
import AIGenButton, {
  useAIGenButtonState,
} from "../../customer/components/AIGenButton"
import { AUTOCOMPLETE_OPTIONS } from "../../customer/utils"
import { adminLiquidTabsSx } from "../adminStyles"
import { adminApi } from "../axios"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import GenerateInboxTemplatePromptPopover from "../components/GenerateInboxTemplatePromptPopover"
import InboxTemplateStatusChip from "../components/InboxTemplateStatusChip"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"
import { selectAdminProfile } from "../reducer"

// Approval status lives in the URL so "needs review" views can be shared.
const TEMPLATE_FILTER_SPECS = {
  status: { param: "approval_status" },
}

const TEMPLATE_TOOLBAR_FILTERS = [
  {
    key: "status",
    label: "Status",
    type: "select",
    anyLabel: "All statuses",
    minWidth: 180,
    options: [
      { value: "approved", label: "Approved" },
      { value: "pending", label: "Pending approval" },
      { value: "rejected", label: "Rejected" },
    ],
  },
]

// Tab is in the URL; switching drops the other tab's search/sort/filters.
const TEMPLATE_TAB_SCOPED_PARAMS = ["q", "pi", "sc", "sd", "status"]

function formatDate(value) {
  if (!value) return "—"
  return format(new Date(value), "MMM d, yyyy")
}

export default function AdminInboxSmsTemplates() {
  const [searchParams] = useSearchParams()
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()
  const tab = searchParams.get("tab") === "concierge" ? 1 : 0

  function setTab(nextTab) {
    if (nextTab === tab) return
    const cleared = Object.fromEntries(
      TEMPLATE_TAB_SCOPED_PARAMS.map((param) => [param, null])
    )
    navigateUpdatedSearchParams({
      ...cleared,
      tab: nextTab === 1 ? "concierge" : null,
    })
  }
  const [creating, setCreating] = useState(false)

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="SMS inbox templates"
        subtitle={
          tab === 0
            ? "Default templates every concierge can pick when replying to inbound SMS. New templates stay hidden in CRM Settings until grace@smartsetter.io approves them."
            : "Account-specific SMS templates created by concierge users. Staff admins and super admins can approve them; the approver is recorded for audit."
        }
        actions={
          tab === 0 ? (
            <Button variant="contained" onClick={() => setCreating(true)}>
              New template
            </Button>
          ) : null
        }
      />
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={adminLiquidTabsSx}
      >
        <Tab label="Default templates" />
        <Tab label="Concierge templates" />
      </Tabs>
      {tab === 0 ? (
        <DefaultInboxSmsTemplatesPanel
          creating={creating}
          onCreatingChange={setCreating}
        />
      ) : (
        <ConciergeInboxSmsTemplatesPanel />
      )}
    </Column>
  )
}

function DefaultInboxSmsTemplatesPanel({ creating, onCreatingChange }) {
  const profile = useSelector(selectAdminProfile)
  const canApprove = Boolean(profile?.can_approve_inbox_templates)
  const [editing, setEditing] = useState(null)
  const [deleteTemplate, setDeleteTemplate] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [actingId, setActingId] = useState(null)

  const query = useAdminPaginatedQuery(adminApi.fetchInboxSmsTemplates, {
    defaultSortColumn: "name",
    defaultSortDirection: "asc",
    filters: TEMPLATE_FILTER_SPECS,
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  async function handleDelete() {
    if (!deleteTemplate) return
    setDeleting(true)
    try {
      await adminApi.deleteInboxSmsTemplate(deleteTemplate.id)
      setDeleteTemplate(null)
      refresh()
    } finally {
      setDeleting(false)
    }
  }

  async function handleApproval(template, action) {
    setActingId(template.id)
    try {
      if (action === "approve") {
        await adminApi.approveInboxSmsTemplate(template.id)
      } else {
        await adminApi.rejectInboxSmsTemplate(template.id)
      }
      refresh()
    } finally {
      setActingId(null)
    }
  }

  return (
    <Column gap={2}>
      <AdminListToolbar
        query={query}
        searchLabel="Search name or message"
        noun={totalResultsSize === 1 ? "template" : "templates"}
        filters={TEMPLATE_TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={5}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="name"
              label="Name"
              query={query}
              minWidth={{ xs: 96, sm: 140 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 220 }}>
              Message
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="approval_status"
              label="Status"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminSortableHeaderCell
              columnName="created"
              label="Created"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminHeaderTableCell align="right" minWidth={{ xs: 96, sm: 120 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((template) => (
          <AdminTableRow key={template.id} hover>
            <AdminTruncatedCell value={template.name} limit={32} />
            <AdminTruncatedCell value={template.text} limit={48} />
            <TableCell sx={adminTableBodyCellSx}>
              <InboxTemplateStatusChip status={template.approval_status} />
            </TableCell>
            <AdminTruncatedCell value={formatDate(template.created)} />
            <TableCell align="right" sx={adminTableBodyCellSx}>
              <Row gap={0.5} justifyContent="flex-end">
                {canApprove && template.approval_status === "pending" && (
                  <>
                    <IconButton
                      size="small"
                      aria-label="Approve template"
                      disabled={actingId === template.id}
                      onClick={() => handleApproval(template, "approve")}
                    >
                      <CheckOutlinedIcon fontSize="small" color="success" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Reject template"
                      disabled={actingId === template.id}
                      onClick={() => handleApproval(template, "reject")}
                    >
                      <CloseOutlinedIcon fontSize="small" color="error" />
                    </IconButton>
                  </>
                )}
                <IconButton
                  size="small"
                  aria-label="Edit template"
                  onClick={() => setEditing(template)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Delete template"
                  onClick={() => setDeleteTemplate(template)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Row>
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>

      <InboxSmsTemplateDialog
        open={creating || Boolean(editing)}
        template={editing}
        onClose={() => {
          onCreatingChange(false)
          setEditing(null)
        }}
        onSaved={() => {
          onCreatingChange(false)
          setEditing(null)
          refresh()
        }}
      />

      <Dialog
        open={Boolean(deleteTemplate)}
        onClose={() => setDeleteTemplate(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete SMS inbox template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete default template <strong>{deleteTemplate?.name}</strong>?
            Concierges will no longer see it when replying to inbound SMS.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDeleteTemplate(null)}>
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={deleting}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Column>
  )
}

function ConciergeInboxSmsTemplatesPanel() {
  const profile = useSelector(selectAdminProfile)
  const canApprove = Boolean(profile?.is_staff || profile?.is_superuser)
  const [actingId, setActingId] = useState(null)

  const query = useAdminPaginatedQuery(
    adminApi.fetchConciergeInboxSmsTemplates,
    {
      defaultSortColumn: "created",
      defaultSortDirection: "desc",
      filters: TEMPLATE_FILTER_SPECS,
    }
  )
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  async function handleApproval(template, action) {
    setActingId(template.id)
    try {
      if (action === "approve") {
        await adminApi.approveConciergeInboxSmsTemplate(template.id)
      } else {
        await adminApi.rejectConciergeInboxSmsTemplate(template.id)
      }
      refresh()
    } finally {
      setActingId(null)
    }
  }

  return (
    <Column gap={2}>
      <AdminListToolbar
        query={query}
        searchLabel="Search name, message, or parent"
        noun={totalResultsSize === 1 ? "template" : "templates"}
        filters={TEMPLATE_TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={6}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="name"
              label="Name"
              query={query}
              minWidth={{ xs: 96, sm: 140 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 180 }}>
              Message
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 160 }}>
              Parent account
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="approval_status"
              label="Status"
              query={query}
              minWidth={{ xs: 72, sm: 96 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 140 }}>
              Approved by
            </AdminHeaderTableCell>
            <AdminHeaderTableCell align="right" minWidth={{ xs: 72, sm: 96 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((template) => (
          <AdminTableRow key={template.id} hover>
            <AdminTruncatedCell value={template.name} limit={32} />
            <AdminTruncatedCell value={template.text} limit={40} />
            <AdminTruncatedCell
              value={
                template.parent_name
                  ? `${template.parent_name} (${template.parent_email || ""})`
                  : template.parent_email
              }
              limit={40}
            />
            <TableCell sx={adminTableBodyCellSx}>
              <InboxTemplateStatusChip status={template.approval_status} />
            </TableCell>
            <AdminTruncatedCell value={template.approved_by_email} limit={32} />
            <TableCell align="right" sx={adminTableBodyCellSx}>
              {canApprove && template.approval_status === "pending" ? (
                <Row gap={0.5} justifyContent="flex-end">
                  <IconButton
                    size="small"
                    aria-label="Approve template"
                    disabled={actingId === template.id}
                    onClick={() => handleApproval(template, "approve")}
                  >
                    <CheckOutlinedIcon fontSize="small" color="success" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Reject template"
                    disabled={actingId === template.id}
                    onClick={() => handleApproval(template, "reject")}
                  >
                    <CloseOutlinedIcon fontSize="small" color="error" />
                  </IconButton>
                </Row>
              ) : null}
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>
    </Column>
  )
}

function InboxSmsTemplateDialog({ open, template, onClose, onSaved }) {
  const [name, setName] = useState("")
  const [text, setText] = useState("")
  const [saving, setSaving] = useState(false)
  const [usedAI, setUsedAI] = useState(false)
  const [aiPrompt, setAiPrompt] = useState("")
  const [menuAnchor, setMenuAnchor] = useState(null)
  const textRef = useRef(null)
  const { aiGenerateButtonRef, setAIGenerateButtonRef } = useAIGenButtonState()

  useEffect(() => {
    if (!open) return
    setName(template?.name || "")
    setText(template?.text || "")
    setUsedAI(false)
    setAiPrompt(template?.ai_prompt || "")
  }, [open, template])

  function insertPlaceholder(fieldName) {
    const token = `{{ ${fieldName} }}`
    const input = textRef.current
    const start = input?.selectionStart ?? text.length
    const end = input?.selectionEnd ?? text.length
    setText(`${text.slice(0, start)}${token}${text.slice(end)}`)
    setMenuAnchor(null)
  }

  function handleGenerated(generated) {
    setAIGenerateButtonRef(null)
    if (!generated) return
    setUsedAI(true)
    setAiPrompt(generated.prompt || "")
    if (generated.title) setName(generated.title)
    if (generated.content) setText(generated.content)
  }

  async function handleSave() {
    if (!name.trim() || !text.trim()) return
    setSaving(true)
    const payload = {
      name: name.trim(),
      text: text.trim(),
      ai_generated: usedAI,
      ai_prompt: aiPrompt,
    }
    try {
      if (template) {
        await adminApi.updateInboxSmsTemplate(template.id, payload)
      } else {
        await adminApi.createInboxSmsTemplate(payload)
      }
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const canSave = Boolean(name.trim()) && Boolean(text.trim())
  const saveLabel = template
    ? "Save and submit for approval"
    : "Submit for approval"

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {template ? "Edit SMS inbox template" : "New SMS inbox template"}
      </DialogTitle>
      <DialogContent>
        <Column sx={{ gap: 2, pt: 1 }}>
          {!template && (
            <Alert severity="info">
              This template will stay pending until grace@smartsetter.io
              approves it. Only then does it appear in CRM Settings as a default
              SMS inbox template.
            </Alert>
          )}
          {template?.approval_status === "pending" && (
            <Alert severity="warning">
              Waiting for approval from grace@smartsetter.io. Concierges cannot
              use this template until it is approved.
            </Alert>
          )}
          {template?.approval_status === "rejected" && (
            <Alert severity="error">
              This template was rejected. Edit it and submit again for approval.
            </Alert>
          )}
          <Row sx={{ gap: 1, alignItems: "flex-start" }}>
            <LengthLimitedTextField
              label="Template name"
              value={name}
              setValue={setName}
              maxLength={128}
              required
            />
            <AIGenButton
              campaign={null}
              setAIGenerateButtonRef={setAIGenerateButtonRef}
            />
            <GenerateInboxTemplatePromptPopover
              anchorElem={aiGenerateButtonRef}
              onClose={handleGenerated}
              generateFn={adminApi.generateInboxSmsTemplate}
            />
          </Row>
          <TextField
            inputRef={textRef}
            label="Message"
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            minRows={6}
            required
            fullWidth
          />
          <Row>
            <Button
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ textTransform: "none" }}
            >
              Insert placeholder
            </Button>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              {AUTOCOMPLETE_OPTIONS.map((option) => (
                <MenuItem
                  key={option.fieldName}
                  onClick={() => insertPlaceholder(option.fieldName)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </Row>
        </Column>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={!canSave}
          onClick={handleSave}
        >
          {saveLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
